"use client";
import React, {
  SVGLineElementAttributes,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import clsx from "clsx";
import scrollama from "scrollama";

import { notEmpty } from "src/lib/utils/notEmpty";

import { getColor } from "./ColorText";
import styles from "./CashFlowSVG.module.css";

const ln = (
  [x1, y1]: [number | string, number | string],
  [x2, y2]: [number | string, number | string],
  { key, stroke, ...props }: SVGLineElementAttributes<SVGLineElement> = {},
) => (
  <line
    {...props}
    stroke={stroke || "currentColor"}
    key={key}
    x1={x1}
    x2={x2}
    y1={y1}
    y2={y2}
  />
);

const nperTSpan = (txt: string) => (
  <tspan fill={getColor("NPER")!} fontSize=".7em" dy="4">
    {txt}
  </tspan>
);

export const CashFlowSVG = ({ animate = true }) => {
  const [beginnigPmt, setBeginningPmt] = useState(false);
  const [showExample, setShowExample] = useState(false);
  const vbHeight = 300;
  const vbWidth = 600; // (16 * vbHeight) / 9;
  const linePattern = useMemo(() => {
    return (beginnigPmt ? "lll.l " : " lll.l").split("") as (" " | "l" | ".")[];
  }, [beginnigPmt]);
  const [pvTxt, fvTxt, rateTxt, nperTxt] = showExample
    ? [3000, 100000, 0.1, 5]
    : ["PV", "FV", "RATE", "NPER"];

  const periodRange = [50, vbWidth - 70];
  const periodPad =
    (periodRange[1] - periodRange[0]) /
    linePattern.filter((x) => x === "l").length;
  const getXPos = useCallback(
    (i: number) => {
      const [start] = periodRange;
      let lIdx = 0;
      const idxs = linePattern.map((x) =>
        ["l", " "].includes(x) ? lIdx++ : lIdx,
      );
      return start + periodPad * idxs[i];
    },
    [linePattern],
  );
  const baseLineY = 200;

  const lines = linePattern
    .map((x, i) => {
      switch (x) {
        case " ": {
          return null;
        }
        case "l": {
          const xPos = getXPos(i);
          return ln([xPos, 100], [xPos, 200], { key: i });
        }
        case ".": {
          const pos = getXPos(i - 1) + periodPad / 2;
          return (
            <text
              key={i}
              fill="currentColor"
              fontSize=".9em"
              x={pos}
              y="150"
              textAnchor="middle"
            >
              ...
            </text>
          );
        }
        default:
          throw Error("case not handled");
      }
    })
    .filter(notEmpty);

  const pmtLabels = linePattern.map((x, i) => {
    const xPos = getXPos(i);
    if (x === "l") {
      return (
        <text
          key={i}
          fill={getColor("PMT")}
          fontSize=".6em"
          x={xPos}
          y={100 - 10}
          textAnchor="middle"
        >
          PMT
        </text>
      );
    }
  });

  const pvLine = ln([getXPos(0), baseLineY], [getXPos(0), baseLineY + 30], {
    stroke: "currentColor",
  });
  const pvLabel = (
    <text
      fontSize=".6em"
      x={getXPos(0) - 5}
      y={baseLineY + 25}
      fill={getColor("PV")}
      textAnchor="end"
    >
      {pvTxt}
    </text>
  );
  const timeLabels = [0, 1, 2, 3].map((x) => {
    return (
      <text
        key={x}
        fontSize=".7em"
        x={getXPos(x) + 5}
        y={baseLineY + 25}
        fill="currentColor"
      >
        t
        <tspan fontSize=".7em" dy="4">
          {beginnigPmt && x === 3 ? nperTSpan("NPER - 1") : x}
        </tspan>
      </text>
    );
  });
  const nperLabel = (
    <text
      fontSize=".7em"
      x={getXPos(5) - 5}
      y={baseLineY + 25}
      fill="currentColor"
      textAnchor="end"
    >
      t
      <tspan fill={getColor("NPER")!} fontSize=".7em" dy="4">
        {nperTxt}
      </tspan>
    </text>
  );

  const rateLabels = [1, 2, 3].map((x) => {
    const xPos = getXPos(x);
    return (
      <text
        key={x}
        fill="currentColor"
        fontSize=".6em"
        x={xPos - 5}
        y={200 - 10}
        textAnchor="end"
      >
        <tspan fontSize=".9em">x</tspan>(1+
        <tspan fill={getColor("RATE")}>{rateTxt}</tspan>)
      </text>
    );
  });

  const fvLine = ln([getXPos(5), baseLineY], [getXPos(5), baseLineY + 30], {
    stroke: "currentColor",
  });
  const fvLabel = (
    <text
      fontSize=".6em"
      x={getXPos(5) + 5}
      y={baseLineY + 25}
      fill={getColor("FV") || "currentColor"}
      textAnchor="start"
    >
      {fvTxt}
    </text>
  );

  const timeLabel = (
    <text
      fontSize=".6em"
      x={getXPos(5) + 10}
      y={baseLineY - 5}
      fill="currentColor"
      textAnchor="start"
    >
      time
    </text>
  );

  const mh = 8;
  const setClass = (el: Element, cls: string, enable: boolean) => {
    enable ? el.classList.add(cls) : el.classList.remove(cls);
  };

  useEffect(() => {
    if (!animate) return;

    const scroller = scrollama();
    const whenDueFocus = (el: Element) => {
      setClass(el, "focus-marker", true);
      setBeginningPmt(true);
    };
    const exampleFocus = (el: Element) => {
      setClass(el, "focus-marker", true);
      setShowExample(true);
    };
    const clearFocus = (el: Element) => {
      setClass(el, "focus-marker", false);
      setShowExample(false);
      setBeginningPmt(false);
    };
    scroller
      .setup({
        step: ".step",
        offset: 0.4,
      })
      .onStepEnter(({ element }) => {
        switch (element.dataset.step) {
          case "when-due":
            whenDueFocus(element);
            return;
          case "example":
            exampleFocus(element);
            return;
        }
      })
      .onStepExit(({ element }) => {
        clearFocus(element);
      });

    const onWhenDueFocus = (ev: Event) => {
      ev.stopPropagation();
      if (!(ev?.target instanceof Element)) return;
      whenDueFocus(ev.target);
    };
    const onExampleFocus = (ev: Event) => {
      ev.stopPropagation();
      if (!(ev?.target instanceof Element)) return;
      exampleFocus(ev.target);
    };
    const onRemoveFocus = (ev: Event) => {
      ev.stopPropagation();
      if (!(ev?.target instanceof Element)) return;
      clearFocus(ev.target);
    };

    document
      .querySelector('[data-step="when-due"]')
      ?.addEventListener("mouseenter", onWhenDueFocus);
    document
      .querySelector('[data-step="when-due"]')
      ?.addEventListener("mouseleave", onRemoveFocus);
    document
      .querySelector('[data-step="example"]')
      ?.addEventListener("mouseenter", onExampleFocus);
    document
      .querySelector('[data-step="example"]')
      ?.addEventListener("mouseleave", onRemoveFocus);

    return () => {
      document
        .querySelector('[data-step="when-due"]')
        ?.removeEventListener("mouseenter", onWhenDueFocus);
      document
        .querySelector('[data-step="when-due"]')
        ?.removeEventListener("mouseleave", onRemoveFocus);
      document
        .querySelector('[data-step="example"]')
        ?.removeEventListener("mouseenter", onExampleFocus);
      document
        .querySelector('[data-step="example"]')
        ?.removeEventListener("mouseleave", onRemoveFocus);
      scroller.destroy();
    };
  }, []);

  return (
    <>
      <svg
        className={clsx(
          "text-gray-400 bg-slate-100 dark:bg-[#3B465C] text-xl font-mono shadow dark:shadow-slate-900 dark:shadow-lg",
          animate && styles.cashflow_svg,
        )}
        viewBox={`0 55 ${vbWidth} ${vbHeight - 100}`}
      >
        <defs>
          <marker
            id="head"
            orient="auto"
            markerWidth={mh / 2 + 1}
            markerHeight={mh}
            refX="0.1"
            refY={mh / 2}
          >
            <path
              d={`M0,0 V${mh} L${mh / 2},${mh / 2} Z`}
              fill="currentColor"
            />
          </marker>
        </defs>

        <line
          markerEnd="url(#head)"
          x1="0"
          y1="200"
          x2={`${vbWidth - 10}`}
          y2="200"
          stroke="currentColor"
        />
        {lines}
        {pvLine}
        {pvLabel}
        {fvLine}
        {timeLabels}
        {nperLabel}
        {fvLabel}
        {pmtLabels}
        {rateLabels}
        {timeLabel}
      </svg>
    </>
  );
};
